import { DropdownShortcut } from "@nms-angular-toolkit/nms-dropdown";
import { PagingFilterType } from "@nms-angular-toolkit/nms-table";

/**
 * Enum que representam os campos para a tabela GPON Onus.
 *
 * Os valores da enum seguem o formato esperado para o campo da tabela, camelCase,
 * diferente das chaves da enum que usam TitleCase.
 */
export enum GponOnusField {
    DeviceName            = "deviceName",
    PortViewName          = "portViewName",
    OnuId                 = "onuId",
    Status                = "status",
    RxPower               = "rxPower",
    TxPower               = "txPower",
    Alarms                = "alarms",
    AlarmsTimes           = "alarmsTimes",
    SerialNumber          = "serialNumber",
    Password              = "password",
    VendorId              = "vendorId",
    EquipmentId           = "equipmentId",
    OnuName               = "onuName",
    OperationalState      = "operationalState",
    PrimaryStatus         = "primaryStatus",
    Distance              = "distance",
    Ipv4Address           = "ipv4Address",
    CwmpCpeIp             = "cwmpCpeIp",
    CwmpCpeLastInform     = "cwmpCpeLastInform",
    Tr069AcsProfile       = "tr069acsProfile",
    Ipv4DefaultGateway    = "ipv4DefaultGateway",
    Ipv4Mode              = "ipv4Mode",
    Ipv4Vlan              = "ipv4Vlan",
    RgProfile             = "rgProfile",
    Snmp                  = "snmp",
    UpstreamFec           = "upstreamFec",
    Uptime                = "uptime",
    LastSeenOnline        = "lastSeenOnline",
    Version               = "version",
    AutoProvisioned       = "autoProvisioned",
    LineProfile           = "lineProfile",
    ServiceProfile        = "serviceProfile",
    ServiceVlans          = "serviceVlans",
    FixedBandwidth        = "fixedBandwidth",
    AssuredFixedBandwidth = "assuredFixedBandwidth",
    ActiveFwFormatted     = "activeFwFormatted",
    StandbyFwFormatted    = "standbyFwFormatted",
    SoftwareDownloadState = "softwareDownloadState",
    LastUpdated           = "lastUpdated",
    Eth1Link              = "eth1Link",
    Eth1InRate            = "eth1InRate",
    Eth1OutRate           = "eth1OutRate",
    Eth2Link              = "eth2Link",
    Eth2InRate            = "eth2InRate",
    Eth2OutRate           = "eth2OutRate",
    Eth3Link              = "eth3Link",
    Eth3InRate            = "eth3InRate",
    Eth3OutRate           = "eth3OutRate",
    Eth4Link              = "eth4Link",
    Eth4InRate            = "eth4InRate",
    Eth4OutRate           = "eth4OutRate"
}

/**
 * Enum que representa os modos de IPV4 na Gpon Onu.
 */
export enum Ipv4Mode {
    DHCP                  = "DHCP",
    STATIC                = "STATIC",
    NOT_CONFIGURED        = "NOT_CONFIGURED"
}

/**
 * Enum que representa os Modos SNMP suportados na Gpon Onu.
 */
export enum SnmpMode {
    ALL                   = "ALL",
    PROFILE               = "PROFILE",
    DISABLED              = "DISABLED"
}

/**
 * Enum que representa os possíveis estados de uma Gpon Onu.
 */
export enum GponOnuStatus {
    NORMAL                = "NORMAL",
    UNKNOWN               = "UNKNOWN",
    MINOR_FAULT           = "MINOR_FAULT",
    MAJOR_FAULT           = "MAJOR_FAULT",
    CRITICAL_FAULT        = "CRITICAL_FAULT",
    DISCOVERED            = "DISCOVERED"
}

/**
 * Enum que representa os status de uma Gpon Onu.
 */
export enum OltStatus {
    NORMAL = "NORMAL",
    UNKNOWN = "UNKNOWN",
    MINOR_FAULT = "MINOR_FAULT",
    MAJOR_FAULT = "MAJOR_FAULT",
    CRITICAL_FAULT = "CRITICAL_FAULT",
    DISABLED = "DISABLED"
}

/**
 * Interface para representar os detalhes do status
 */
export interface StatusDetails {
    color: string;
    label: string;
}

/**
 * Objeto contendo os detalhes de cada status
 */
export const OltStatusInfo: { [key in OltStatus]: StatusDetails } = {
    [OltStatus.NORMAL]: {
        color: "#1d9d23",/** verde */
        label: "status.normal"
    },
    [OltStatus.UNKNOWN]: {
        color: "#347ac1", /** azul */
        label: "status.unknown"
    },
    [OltStatus.MINOR_FAULT]: {
        color: "#dbd20d", /** amarelo */
        label: "status.minorFault"
    },
    [OltStatus.MAJOR_FAULT]: {
        color: "#fa5904", /** vermelho laranja */
        label: "status.majorFault"
    },
    [OltStatus.CRITICAL_FAULT]: {
        color: "#b12525", /** vermelho forte */
        label: "status.criticalFault"
    },
    [OltStatus.DISABLED]: {
        color: "#650000", /** vermelho escuro */
        label: "status.disabled"
    }
};

/**
 * Enum que representa tipos de hardware na Gpon Onu.
 */
export enum HardwareTypeOnu {
    CPU    = "CPU",
    MEMORY = "MEMORY"
}

/**
* Modelo utilizado para preencher os dropdown de acordo com as features disponiveis e de acordo
* com a gpon.
*/
export interface DeviceDropdownShortcut {
    deviceId: string;
    shortcuts: Array<DropdownShortcut>;
}

export type PageFilterFunction = (value) => PagingFilterType;

/**
 * Modelo que representa o mapeamento entre strings e suas respectivas funções.
 */
export interface FilterTypeMapping {
    [key: string]: PageFilterFunction
}

/**
 * Modelo que representa o mapeamento entre @see GponOnusField e um valor desejado (@see T).
 */
export type FieldMapping<T> = {
    [key in GponOnusField]?: T
}
